import Input from '@components/forms/Input'
import Label from '@components/forms/Label'
import Button, { IconButton } from '@components/shared/Button'
import InlineNotification from '@components/shared/InlineNotification'
import Loading from '@components/loading/Loading'
import { PencilIcon, PlusIcon } from '@heroicons/react/20/solid'
import { useWallet } from '@solana/wallet-adapter-react'
import { ChangeEvent, useEffect, useRef, useState, useCallback } from 'react'
import {
  isUsersApiEnabled,
  USER_TOKEN_KEY,
  USERS_API_URL,
} from 'utils/constants'
import { notify } from 'utils/notifications'
import ProfileImage from './ProfileImage'
import { useIsAuthorized } from 'hooks/useIsAuthorized'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import { login } from 'utils/login'
import store from 'stores/store'
import { sleep } from 'utils'
import { useProfileDetails } from './useProfileDetails'
import ButtonGroup from '@components/forms/ButtonGroup'
import { useTranslation } from 'next-i18next'
import { tryStringify } from 'utils/governance/tools'

type ProfileUpdateError = {
  error: string
  status: number
}

interface EditProfileFormProps {
  onFinish: () => void
}

const EditProfileForm = ({ onFinish }: EditProfileFormProps) => {
  const { t } = useTranslation(['profile', 'messages'])
  const { authenticated, loadingUser } = useIsAuthorized()
  const { profileDetails, refetchProfileDetails } = useProfileDetails()
  const [token, setToken] = useLocalStorage(USER_TOKEN_KEY)
  const { publicKey, signMessage, signTransaction } = useWallet()
  const [username, setUsername] = useState('')
  const [loadUniquenessCheck, setLoadUniquenessCheck] = useState(false)
  const [loadUpdateProfile, setLoadUpdateProfile] = useState(false)
  const [usernameError, setUsernameError] = useState('')
  const [imageError, setImageError] = useState('')
  const [updateError, setUpdateError] = useState('')
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUsingLedger, setIsUsingLedger] = useState<'Yes' | 'No'>('No')

  // set form with existing profile details
  useEffect(() => {
    if (profileDetails?.username) {
      setUsername(profileDetails.username)
    }
  }, [profileDetails])

  useEffect(() => {
    if (selectedImage == null) {
      setSelectedImageUrl(null)
      return
    }

    const objectUrl = URL.createObjectURL(selectedImage)
    setSelectedImageUrl(objectUrl)

    return () => URL.revokeObjectURL(objectUrl)
  }, [selectedImage])

  const handleImageSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      const validTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/webp',
        'image/gif',
        'image/svg+xml',
      ]
      if (!validTypes.includes(file.type)) {
        setImageError(t('form.image-error-type'))
        return
      }

      const MAX_FILE_SIZE_KiB = 1024 * 1024 /* 1 MiB */
      if (file.size > MAX_FILE_SIZE_KiB) {
        setImageError(t('form.image-error-size'))
        return
      }

      setSelectedImage(file)
    },
    [t],
  )

  const onEditProfileImage = useCallback(() => {
    fileInputRef.current?.click()
    if (updateError) setUpdateError('')
    if (imageError) setImageError('')
  }, [imageError, updateError])

  const validateUsername = useCallback(
    async (name: string, authToken: string): Promise<boolean> => {
      const re = /^[a-zA-Z0-9 ]*$/gm
      if (!re.test(name)) {
        setUsernameError(t('form.username-invalid'))
        return false
      }
      if (name.length < 3 || name.length > 20) {
        setUsernameError(t('form.username-length'))
        return false
      }

      if (!isUsersApiEnabled()) {
        setUsernameError('Profile service is unavailable')
        return false
      }

      try {
        setLoadUniquenessCheck(true)
        const response = await fetch(
          `${USERS_API_URL}/user/check-username/${name}`,
          {
            headers: {
              'Content-Type': 'application/json',
              authorization: authToken || '',
            },
          },
        )
        const uniquenessCheck = (await response.json()) as {
          available: boolean
        }
        if (uniquenessCheck?.available) {
          return true
        } else {
          setUsernameError(t('form.username-unavailable'))
          return false
        }
      } catch (error) {
        setUsernameError(t('form.username-unavailable'))
        return false
      } finally {
        setLoadUniquenessCheck(false)
      }
    },
    [t],
  )

  const onChangeNameInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const name = e.target.value
      setUsername(name)
      if (usernameError) setUsernameError('')
      if (updateError) setUpdateError('')
    },
    [updateError, usernameError],
  )

  const handleAuthError = useCallback(
    (error: unknown) => {
      setUpdateError(t('form.error'))
      setLoadUpdateProfile(false)
      console.error('Authentication error:', error)
    },
    [t],
  )

  const handleProfileUpdateError = useCallback(
    (error: unknown) => {
      setUpdateError(t('form.error'))
      setLoadUpdateProfile(false)
      console.error(
        'Profile update error:',
        error instanceof Error ? error : (tryStringify(error) ?? String(error)),
      )
    },
    [t],
  )

  const saveProfile = useCallback(async () => {
    const connection = store.getState().connection
    setUpdateError('')
    const trimmedUsername = username.trim().toLowerCase()

    // early return if no changes
    if (profileDetails?.username === trimmedUsername && !selectedImage) {
      onFinish()
      return
    }

    if (!publicKey || !signMessage || !signTransaction) {
      setUpdateError(t('messages:errors.wallet-not-connected'))
      return
    }

    let authToken = token
    setLoadUpdateProfile(true)

    // handle authentication if needed
    if (!authenticated) {
      const loginType = isUsingLedger === 'Yes' ? 'ledger' : 'siws'
      try {
        const authResponse = await login(
          loginType,
          { publicKey, signMessage, signTransaction },
          connection,
          { errorTitle: t('messages:errors.error') },
        )
        if (!authResponse?.token) {
          setLoadUpdateProfile(false)
          return
        }
        authToken = authResponse.token
        setToken(authToken)
      } catch (error) {
        handleAuthError(error)
        return
      }
    }

    // validate username if needed
    const updateUsername =
      !profileDetails?.username || profileDetails?.username !== trimmedUsername
    let isValidUsername = false
    if (updateUsername && authToken) {
      try {
        isValidUsername = await validateUsername(trimmedUsername, authToken)
      } catch (error) {
        handleProfileUpdateError(error)
        return
      }
    }

    // update profile if validation passes
    if (
      (!usernameError && isValidUsername) ||
      (!updateUsername && selectedImage)
    ) {
      try {
        const formData = new FormData()

        if (updateUsername) {
          formData.append('username', trimmedUsername)
        }

        if (selectedImage) {
          formData.append('image', selectedImage)
        }

        // early return if no changes to submit
        if (formData.entries().next().done) {
          setLoadUpdateProfile(false)
          onFinish()
          return
        }

        const requestOptions = {
          method: !profileDetails ? 'POST' : 'PUT',
          headers: {
            authorization: authToken || '',
          },
          body: formData,
        }

        if (!isUsersApiEnabled()) {
          notify({
            type: 'error',
            title: 'Profile service is unavailable',
          })
          setLoadUpdateProfile(false)
          return
        }
        const response = await fetch(`${USERS_API_URL}/user`, requestOptions)

        if (response.ok) {
          await refetchProfileDetails()
          await sleep(1000)
          onFinish()
          notify({
            type: 'success',
            title: t('form.success'),
          })
          setLoadUpdateProfile(false)
        } else {
          const errorData: ProfileUpdateError = {
            error: t('form.error'),
            status: response.status,
          }
          handleProfileUpdateError(errorData)
        }
      } catch (error) {
        const errorData: ProfileUpdateError = {
          error: t('form.error'),
          status: 500,
        }
        handleProfileUpdateError(errorData)
      }
    } else {
      setLoadUpdateProfile(false)
    }
  }, [
    authenticated,
    handleAuthError,
    handleProfileUpdateError,
    isUsingLedger,
    onFinish,
    profileDetails,
    publicKey,
    refetchProfileDetails,
    selectedImage,
    setToken,
    signMessage,
    signTransaction,
    t,
    token,
    username,
    usernameError,
    validateUsername,
  ])

  return (
    <>
      {updateError ? (
        <div className="py-3">
          <InlineNotification type="error" desc={updateError} />
        </div>
      ) : null}
      <div className="mb-4 mt-6 flex flex-col items-center">
        <div className="relative">
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept=".jpg,.jpeg,.png,.webp,.gif,.svg"
            onChange={handleImageSelect}
          />
          <IconButton
            className="absolute -right-2 -top-2 bg-th-button md:hover:bg-th-button-hover"
            size="small"
            onClick={onEditProfileImage}
          >
            {selectedImageUrl || profileDetails?.hasImage ? (
              <PencilIcon className="size-4 text-th-button-text" />
            ) : (
              <PlusIcon className="size-4 text-th-button-text" />
            )}
          </IconButton>
          <ProfileImage
            imageSize="96"
            placeholderSize="56"
            imageSource={{
              type: 'customUrl',
              imageUrl: selectedImageUrl,
              fallbackStrategy: 'currentUser',
            }}
          />
        </div>
        {imageError ? (
          <div className="mt-3">
            <InlineNotification
              type="error"
              desc={imageError}
              hideBorder
              hidePadding
            />
          </div>
        ) : null}
      </div>
      {!authenticated && !loadingUser ? (
        <div className="pb-4">
          <Label text={t('form.ledger')} />
          <ButtonGroup
            activeValue={isUsingLedger}
            values={['Yes', 'No']}
            onChange={setIsUsingLedger}
            names={[t('form.ledger-yes'), t('form.ledger-no')]}
          />
          <div className="mt-1.5">
            <InlineNotification
              type="info"
              desc={t('form.ledger-info')}
              hideBorder
              hidePadding
            />
          </div>
        </div>
      ) : null}
      <div className={'pb-6'}>
        <Label text={t('form.username')} />
        <Input
          type="text"
          value={username}
          onChange={onChangeNameInput}
          maxLength={20}
        />
        {usernameError ? (
          <div className="mt-1.5">
            <InlineNotification
              type="error"
              desc={usernameError}
              hideBorder
              hidePadding
            />
          </div>
        ) : null}
      </div>
      <Button
        className={`mb-6 flex w-full items-center justify-center`}
        disabled={!!usernameError || loadUniquenessCheck || !username}
        onClick={saveProfile}
        size="large"
      >
        {loadUniquenessCheck || loadUpdateProfile ? (
          <Loading />
        ) : (
          t('form.save')
        )}
      </Button>
    </>
  )
}

export default EditProfileForm
